// Release-channel resolution for both the desktop auto-updater and the
// game server it talks to.
//
// Three channels:
//   'stable' (default)
//     - Desktop updater pulls latest-{platform}.yml → stable builds
//     - API/WS point at the production Railway service
//   'beta'
//     - Desktop updater pulls beta-{platform}.yml → RC builds
//     - API/WS point at the staging Railway service (separate DB)
//   'local' (dev only. Hidden from packaged builds)
//     - Updater is skipped (dev builds never auto-update anyway)
//     - API/WS point at a local Node process on port 3000. Useful for
//       iterating on server code without pushing to Railway. A
//       VALKENHALL_SERVER=local env var passed to `bun run dev` boots
//       the renderer straight onto this channel via a ?server=local URL
//       flag, applied as a transient session override that does NOT
//       persist to the pref. Toggling the Local row in Settings does
//       persist it the normal way.
//
// Switching channels invalidates the auth token (different DBs, the
// saved token is meaningless on the other side), so the caller is
// expected to force-log-out the user on channel change.

import { getPref, setPref } from './persistentPrefs';
import { isDevBuild } from './devMode';
import { webBakedChannel } from './platform';

// Dev and packaged builds share one OS-level preferences.json (same app
// name), so they'd otherwise stomp on each other's channel choice.
// Dev builds get a separate key so switching the server in dev doesn't
// move the shipped app off its channel. And vice versa. Packaged builds
// keep the bare 'updateChannel' key the main-process auto-updater reads
// (electron/updater.js); dev builds never auto-update so the split is safe.
const CHANNEL_PREF_KEY = isDevBuild() ? 'updateChannel:dev' : 'updateChannel';
const VALID_CHANNELS = new Set(['stable', 'beta', 'local']);

const PROD_HTTP = 'https://valkenhall-server-production.up.railway.app';
const PROD_WS = 'wss://valkenhall-server-production.up.railway.app/ws';

// Beta / Release Candidate server. Deployed from the server repo's
// `main` by the VPS daily cycle (`~/bin/deploy-beta.sh`, the only
// sanctioned agent deploy path), backed by its own Postgres
// (postgres-beta). Accounts, collections, mail etc. are isolated from
// prod, so switching channels signs the user out of one server and
// forces a fresh login on the other. The settings-screen toggle
// handles that automatically via channelsUseSameServer() returning
// false here.
const BETA_HTTP = 'https://valkenhall-server-beta-production.up.railway.app';
const BETA_WS = 'wss://valkenhall-server-beta-production.up.railway.app/ws';

// "local" targets the machine serving the page, not literally localhost:
// a web build opened from another device (phone on the tailnet hitting
// the dev Mac's IP) must reach the dev server on that same machine.
// Electron and same-machine browsing resolve to localhost as before.
const LOCAL_HOST = (typeof location !== 'undefined' && location.hostname) ? location.hostname : 'localhost';
// Dev-only: `?localPort=<n>` (the Electron shell forwards VALKENHALL_LOCAL_PORT
// in unpackaged runs) points the local channel at a server on another port, so
// a GUI run can drive a self-provisioned server while :3000 stays someone else's.
const LOCAL_PORT = (typeof location !== 'undefined' && /^\d+$/.test(new URLSearchParams(location.search).get('localPort') || ''))
  ? new URLSearchParams(location.search).get('localPort') : '3000';
const LOCAL_HTTP = `http://${LOCAL_HOST}:${LOCAL_PORT}`;
const LOCAL_WS = `ws://${LOCAL_HOST}:${LOCAL_PORT}/ws`;

const URL_BY_CHANNEL = {
  stable: { http: PROD_HTTP, ws: PROD_WS },
  beta: { http: BETA_HTTP, ws: BETA_WS },
  local: { http: LOCAL_HTTP, ws: LOCAL_WS },
};

// Transient session override read once from the renderer URL. The
// Electron main process appends `?server=local` (or stable/beta) when
// the VALKENHALL_SERVER env var is set, so a single invocation like
// `VALKENHALL_SERVER=local bun run dev` flips the renderer onto the
// local server without touching the user's saved pref.
let sessionOverride = null;
try {
  if (typeof window !== 'undefined') {
    const v = new URLSearchParams(window.location.search).get('server');
    if (v && VALID_CHANNELS.has(v)) sessionOverride = v;
  }
} catch (_) {}

export function getUpdateChannel() {
  if (sessionOverride) return sessionOverride;
  // Web builds bake the channel at deploy time (play.valkenhall.com →
  // stable, qa.valkenhall.com → beta). There is no channel UI on web,
  // so the baked value is authoritative rather than a pref default.
  const baked = webBakedChannel();
  if (baked) return VALID_CHANNELS.has(baked) ? baked : 'stable';
  const raw = getPref(CHANNEL_PREF_KEY);
  return VALID_CHANNELS.has(raw) ? raw : 'stable';
}

export function setUpdateChannel(channel) {
  const next = VALID_CHANNELS.has(channel) ? channel : 'stable';
  // Explicit user action takes priority over the URL override.
  // otherwise clicking a different channel in Settings would silently
  // bounce back to the env-var choice on the next read.
  sessionOverride = null;
  setPref(CHANNEL_PREF_KEY, next);
  return next;
}

// The gate for dev-only entry points (Dev Quick Match, vs Bot): an
// unpackaged build AND pointed at the local dev server.
//
// Both halves matter. isDevBuild() alone still exposes them in a dev
// build aimed at production, and the channel alone exposes them in a
// shipped build whose owner flipped Settings to Local. Callers must use
// this rather than re-deriving the pair, so the two entry points cannot
// drift apart.
//
// This only hides UI. The server refuses both independently. A gate
// the client can flip is not a gate.
export function isDevServerBuild() {
  return isDevBuild() && getUpdateChannel() === 'local';
}

// The practice bot is playable on the beta (QA) channel as well as in a dev
// build on the local server. Mirrors the server: it admits botOpponent only
// outside production or on SERVER_CHANNEL=beta.
export function isPracticeBotAvailable() {
  return isDevServerBuild() || getUpdateChannel() === 'beta';
}

// True if the current channel was set by the VALKENHALL_SERVER env var
// (renderer ?server=…) rather than the persisted pref. Settings shows
// a small badge for this so it's obvious why Local is highlighted.
export function isChannelFromEnv() {
  return sessionOverride !== null;
}

export function getServerUrl() {
  return URL_BY_CHANNEL[getUpdateChannel()].http;
}

export function getServerWsUrl() {
  return URL_BY_CHANNEL[getUpdateChannel()].ws;
}

// Exported for tests + the rare caller that wants to display both URLs
// regardless of current channel (e.g. the Settings panel's channel
// picker showing which target each option connects to).
export const CHANNEL_URLS = URL_BY_CHANNEL;

// True while stable and beta point at the same server (e.g. before the
// RC Railway service is provisioned). In this state the channel
// toggle is purely a desktop-updater preference. Switching doesn't
// change which server is used, so we skip the force-logout flow.
export function channelsUseSameServer() {
  return URL_BY_CHANNEL.stable.http === URL_BY_CHANNEL.beta.http;
}
